import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormsConfigActions } from 'app/store/data/formsConfig';
import { selectQuotation } from 'app/store/data/quotation';
import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { isValidCarRegistrationNumber } from 'app/validators/validateCarRegistrationNumber';
import { clearAnonymization } from 'app/shared/helpers/clearAnonymization';

const CAR_REGISTRATION_NUMBER_MAX_LENGTH = 8;

export function useCarRegistrationNumberControl({
  input,
  onClick,
  onControlBlur,
  onValueChange,
}: WrappedControlProps) {
  const dispatch = useDispatch();
  const { id, car } = useSelector(selectQuotation);
  const [focused, setFocused] = useState<boolean>(false);

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e.target.value);
    setFocused(true);
  };

  const isCarRegistrationNumberValid = (value: string): boolean => {
    if (!value) {
      return true;
    }
    return /^[a-z0-9*]+$/i.test(value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length > CAR_REGISTRATION_NUMBER_MAX_LENGTH) {
      return;
    }

    if (!isCarRegistrationNumberValid(value)) {
      return;
    }

    input.onChange(value);

    if (onValueChange) {
      onValueChange(value, false, false);
    }
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearAnonymization(value);

    if (clearedValue !== value) {
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = () => {
    const formattedValue = input.value.toUpperCase();

    const isValid = isValidCarRegistrationNumber(formattedValue);
    const shouldPatch = isValid || formattedValue === '';

    setFocused(false);
    input.onBlur(formattedValue);

    dispatch(
      FormsConfigActions.setPreviousRegistrationNumber({
        [id]: car?.registrationNumber || undefined,
      })
    );

    if (onControlBlur) {
      setTimeout(() => {
        onControlBlur(shouldPatch, true, true, false, formattedValue);
      }, 0);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return {
    maxLength: CAR_REGISTRATION_NUMBER_MAX_LENGTH,
    focused,
    handleBlur,
    handleChange,
    handleClick,
    onFocus,
  };
}
